import Tabs from "./tabs";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Button,
  Stack,
  Typography,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
  Link
} from "@mui/material";
import { FieldTextField, FieldSelect } from "../../../components/selections/fieldInputs";
import FilterMultiSelect from "../../../components/selections/filterMultiSelect";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ApartmentIcon from "@mui/icons-material/Apartment";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { QR, QrActionButton } from "../../qrCode/components/qrUi";
import { stepCardSx, stepActionsSx, backBtnSx } from "../../campaign/components/stepUi";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import SampleExecelFile from '../../../components/data/ExcelFile.xlsx';
import * as XLSX from 'xlsx';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import { APP_ROUTES } from "../../../config/appRoutes";
import { getContactThunk, addContactThunk } from "../contactThunk";
import DataTableWithCheckbox from '../../../components/dataTableWithSearch/DataTableWithCheckBox';

// Single / Bulk segmented toggle
const modeSx = {
  m: 0,
  px: 2,
  height: 36,
  borderRadius: 2.5,
  "& .MuiFormControlLabel-label": { fontSize: 13.5, fontWeight: 600, color: "#64748b" },
  "&:hover": { bgcolor: "#e9edf2" },
  "&:has(.Mui-checked)": {
    bgcolor: "#fff",
    boxShadow: "0 1px 3px rgba(16,24,40,0.12)",
    "& .MuiFormControlLabel-label": { color: "#2563eb", fontWeight: 700 },
  },
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Add-contact form. `embedded` = inside the list page popup (no page header, Cancel button,
// onSuccess(message) closes the popup and the list shows the message).
export function ContactCreateForm({ embedded = false, onCancel, onSuccess }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { branchDepartments } = useSelector((state) => state.general);
  const { getTags, status } = useSelector((state) => state.contacts);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [state, setState] = useState({
    selectedValue: "1",
    userName: "",
    emailAddress: "",
    mobileNumber: "",
    branchDepartmentId: "",
    selectedTags: [],
    errors: {},
    parsedExcelData: [],
  });

  const { selectedValue, userName, emailAddress, mobileNumber, branchDepartmentId, selectedTags, parsedExcelData } = state;

  const handleOptionChange = (event) => {
    setState((prev) => ({
      ...prev,
      selectedValue: event.target.value,
      userName: "",
      emailAddress: "",
      mobileNumber: "",
      branchDepartmentId: "",
      selectedTags: [],
      errors: {},
      parsedExcelData: [],
    }));
  };

  const singleContactsFields = [
    { id: 1, name: "userName", value: userName, type: "text", label: "Name" },
    // { id: 2, name: "emailAddress", value: emailAddress, type: "text", label: "Email" },
    { id: 3, name: "mobileNumber", value: mobileNumber, type: "text", label: "Mobile" },
    { id: 4, name: "branchDepartmentId", value: branchDepartmentId, type: "selection", options: branchDepartments.map((item) => ({ value: item.id, label: item.branch_department_name })), label: "Department" },
    { id: 5, name: "selectedTags", value: selectedTags, type: "multi-select", label: "Tags", options: getTags.map((item) => ({ value: item.id, label: item.tag_name })) }
  ];

  const bulkContactsFields = [
    { id: 1, name: "branchDepartmentId", value: branchDepartmentId, type: "selection", options: branchDepartments.map((item) => ({ value: item.id, label: item.branch_department_name })), label: "Department" },
    { id: 2, name: "selectedTags", value: selectedTags, type: "multi-select", label: "Tags", options: getTags.map((item) => ({ value: item.id, label: item.tag_name })) },
    { id: 3, name: "parsedExcelData", value: "parsedExcelData", type: "excel-file" },
  ];

  const handleChange = (name, value) => {
    let newValue = value;
    if (name === "userName") newValue = value.replace(/^\s+/, "").slice(0, 20);
    else if (name === "emailAddress") newValue = value.replace(/\s/g, "").slice(0, 100);
    else if (name === "mobileNumber") newValue = value.replace(/\D/g, "").slice(0, 10);

    setState((prev) => ({
      ...prev,
      [name]: newValue,
      errors: { ...prev.errors, [name]: "" },
    }));
  };

  const handleMultiSelectChange = (name, selectedValues) => {
    setState((prev) => ({
      ...prev,
      [name]: selectedValues,
      errors: { ...prev.errors, [name]: "" },
    }));
  };

  const handleSingleSelectChange = (name, selectedValue) => {
    setState((prev) => ({
      ...prev,
      [name]: selectedValue,
      errors: { ...prev.errors, [name]: "" },
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      // ✅ Check if the Excel file is empty
      if (!jsonData.length) {
        setSnackbar({
          open: true,
          message: "The uploaded Excel file is empty. Please upload a valid file.",
          severity: "error",
        });
        setState((prev) => ({ ...prev, errors: {}, parsedExcelData: [] }));
        return;
      }

      let errors = [];
      const uniqueMobileSet = new Set();
      const filteredData = [];

      jsonData.forEach((row, index) => {
        const rowNum = index + 1;
        const userName = String(row["Name"] || "").trim();
        const userContact = String(row["Mobile"] || "").trim();
        const userEmail = String(row["Email"] || "").trim();

        // Validation
        const rowValidations = [
          { valid: !!userName, message: "Name is required", field: "Name" },
          { valid: !!userContact, message: "Mobile number is required", field: "Mobile" },
          { valid: /^[6-9]\d{9}$/.test(userContact), message: "Enter a valid 10-digit mobile number", field: "Mobile" },
        ];

        rowValidations.forEach((v) => {
          if (!v.valid) errors.push(`Row ${rowNum} (${v.field}): ${v.message}`);
        });

        // ✅ Deduplicate by Mobile number
        if (!uniqueMobileSet.has(userContact) && /^[6-9]\d{9}$/.test(userContact)) {
          uniqueMobileSet.add(userContact);
          filteredData.push({
            Name: userName,
            Mobile: userContact,
            Email: userEmail,
          });
        }
      });

      // ✅ Show validation errors if any
      if (errors.length > 0) {
        setSnackbar({ open: true, message: errors.join("\n"), severity: "error" });
        setState((prev) => ({ ...prev, errors: {}, parsedExcelData: [] }));
        return;
      }

      // ✅ Check if no valid or unique data remains
      if (filteredData.length === 0) {
        setSnackbar({
          open: true,
          message: "No valid contact data found in the Excel file.",
          severity: "error",
        });
        setState((prev) => ({ ...prev, errors: {}, parsedExcelData: [] }));
        return;
      }

      // ✅ Update parsed data with duplicates removed
      setState((prev) => ({ ...prev, errors: {}, parsedExcelData: filteredData }));
    };

    reader.readAsArrayBuffer(file);
  };


  const renderBranchDept = (field) => (
    <FieldSelect
      name={field.name}
      label={field.label}
      options={field.options}
      value={field.value}
      onChange={handleSingleSelectChange}
      error={!!state.errors[field.name]}
      icon={<ApartmentIcon />}
      placeholder="Select department"
      required
    />
  );

  const renderTags = (field) => (
    <FilterMultiSelect
      name={field.name}
      label={field.label}
      options={field.options}
      value={state[field.name] || []}
      onChange={handleMultiSelectChange}
      formHelperText={false}
      error={!!state.errors[field.name]}
    />
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    let extraValidations = [];

    if (selectedValue === "2") {
      extraValidations = [
        { valid: !!branchDepartmentId, message: "Branch Dept is required", field: "branchDepartmentId" },
        { valid: selectedTags.length > 0, message: "Select at least one Tag", field: "selectedTags" },
        { valid: parsedExcelData.length > 0, message: "Upload data is required", field: "parsedExcelData" },
      ];
    } else {
      extraValidations = [
        { valid: !!userName, message: "Name is required", field: "userName" },
        { valid: !!mobileNumber, message: "Contact number is required", field: "mobileNumber" },
        { valid: /^[6-9]\d{9}$/.test(mobileNumber), message: "Enter a valid 10-digit mobile number", field: "mobileNumber" },
        { valid: !!branchDepartmentId, message: "Branch Dept is required", field: "branchDepartmentId" },
        { valid: selectedTags.length > 0, message: "Select at least one Tag", field: "selectedTags" },
      ];
    }

    for (let rule of extraValidations) {
      if (!rule.valid) {
        setSnackbar({ open: true, message: rule.message, severity: "error" });
        setState((prev) => ({ ...prev, errors: { ...prev.errors, [rule.field]: rule.message } }));
        return;
      }
    }

    const payload = {
      tag_id: selectedTags.map((item) => item.value).toString(),
      branch_department_id: branchDepartmentId.toString(),
      user: selectedValue === "2" ? parsedExcelData.map((item) => ({ mobile: `91${item.Mobile}`, email: item.Email, name: item.Name })) : [{ mobile: `91${mobileNumber}`, email: emailAddress, name: userName }]
    };

    try {
      const result = await dispatch(addContactThunk({ credentials: payload }));
      if (result.meta.requestStatus === "fulfilled") {
        if (onSuccess) {
          dispatch(getContactThunk({ type: "getContact" }));
          onSuccess(result.payload?.message);
          return;
        }
        setSnackbar({ open: true, message: result.payload.message, severity: "success" });
        dispatch(getContactThunk({ type: "getContact" }));
        navigate(APP_ROUTES.CONTACT.LIST);
      } else {
        setSnackbar({ open: true, message: result.payload, severity: "error" });
      }
    } catch {
      setSnackbar({ open: true, message: "Server error. Please try again.", severity: "error" });
    }
  };

  // Table columns for bulk upload preview
  const tableColumns = [
    { id: 'Name', name: 'Name', label: 'Name' },
    { id: 'Email', name: 'Email', label: 'Email' },
    { id: 'Mobile', name: 'Mobile', label: 'Mobile' },
  ];

  const columns = [  
    { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
    { accessorKey: "Name", header: "Name" },
    // { accessorKey: "Email", header: "Email" },
    { accessorKey: "Mobile", header: "Mobile" },
  ];

  const fieldIcons = { userName: <PersonOutlineIcon />, mobileNumber: <PhoneIphoneIcon />, emailAddress: <MailOutlineIcon /> };
  const fieldPlaceholders = { userName: "Contact name", mobileNumber: "10-digit mobile number", emailAddress: "name@example.com" };

  const submitBar = (
    <Box sx={stepActionsSx}>
      <Stack direction="row" spacing={1.5}>
        {embedded && (
          <Button onClick={onCancel} sx={backBtnSx}>Cancel</Button>
        )}
        <Box>
          <QrActionButton
            variant="solid"
            color={QR.blue}
            gradientTo="#3b82f6"
            icon={<PersonAddAlt1Icon sx={{ fontSize: 18 }} />}
            label="Create Contact"
            loading={status === "loading"}
            loadingLabel="Submitting.."
            onClick={handleSubmit}
          />
        </Box>
      </Stack>
    </Box>
  );

  const Wrap = embedded ? Box : Tabs;

  return (
    <div>
      <Wrap>
        {/* Single / Bulk toggle */}
        <Box sx={{ mt: embedded ? 0 : 1.5, mb: 2, ...(embedded && { px: { xs: 2, sm: 3 }, pt: 2 }) }}>
          <FormControl component="fieldset">
            <RadioGroup
              aria-labelledby="my-radio-group-label"
              name="my-radio-group"
              value={selectedValue}
              onChange={handleOptionChange}
              row
              sx={{ p: 0.5, gap: 0.5, borderRadius: 3, bgcolor: "#f1f5f9", border: `1px solid ${QR.border}` }}
            >
              <FormControlLabel value="1" control={<Radio sx={{ display: "none" }} />} label={<Stack direction="row" spacing={0.75} alignItems="center"><PersonOutlineIcon sx={{ fontSize: 18 }} /><span>Single</span></Stack>} sx={modeSx} />
              <FormControlLabel value="2" control={<Radio sx={{ display: "none" }} />} label={<Stack direction="row" spacing={0.75} alignItems="center"><GroupAddOutlinedIcon sx={{ fontSize: 18 }} /><span>Bulk</span></Stack>} sx={modeSx} />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box sx={embedded ? { bgcolor: "#fff" } : { ...stepCardSx, bgcolor: "#fff" }}>
          {selectedValue === "1" ? (
            <>
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Grid container spacing={2.5}>
                  {singleContactsFields.map((field, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                      {field.type === "text" ? (
                        <FieldTextField
                          name={field.name}
                          label={field.label}
                          value={state[field.name]}
                          onChange={(e) => handleChange(field.name, e.target.value)}
                          error={!!state.errors[field.name]}
                          maxLength={field.name === "mobileNumber" ? 10 : field.name === "userName" ? 20 : field.name === "emailAddress" ? 100 : undefined}
                          onKeyDown={field.name === "emailAddress" ? (e) => { if (e.key === " " || e.key === "Spacebar") e.preventDefault(); } : undefined}
                          icon={fieldIcons[field.name]}
                          placeholder={fieldPlaceholders[field.name]}
                          required
                        />
                      ) : field.type === "selection" ? (
                        renderBranchDept(field)
                      ) : field.type === "multi-select" ? (
                        renderTags(field)
                      ) : null}
                    </Grid>
                  ))}
                </Grid>
              </Box>
              {submitBar}
            </>
          ) : (
            <>
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Grid container spacing={2.5}>
                  {bulkContactsFields.map((field, index) => (
                    <Grid size={{ xs: 12, sm: field.type === "excel-file" ? 12 : 6, md: 4 }} key={index}>
                      {field.type === "excel-file" ? (
                        <Box>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 500, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b" }}>
                              Excel File <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
                            </Typography>
                            <Link
                              href={SampleExecelFile}
                              underline="hover"
                              sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, fontSize: 12.5, fontWeight: 700, color: QR.blue, whiteSpace: "nowrap" }}
                            >
                              <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                              Sample File
                            </Link>
                          </Stack>
                          <Box
                            component="label"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                              px: 1.5,
                              minHeight: 42,
                              borderRadius: 2.5,
                              cursor: "pointer",
                              width: "100%",
                              border: `1.5px dashed ${state.errors[field.name] ? "#ef4444" : parsedExcelData.length ? "#86efac" : "#93c5fd"}`,
                              bgcolor: parsedExcelData.length ? "#f0fdf4" : "#f8fbff",
                              transition: "all .15s ease",
                              "&:hover": { borderColor: state.errors[field.name] ? "#ef4444" : QR.blue, bgcolor: parsedExcelData.length ? "#ecfdf5" : "#eff6ff" },
                            }}
                          >
                            {parsedExcelData.length
                              ? <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#16a34a" }} />
                              : <CloudUploadIcon sx={{ fontSize: 20, color: QR.blue }} />}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
                                {parsedExcelData.length ? `${parsedExcelData.length} contacts loaded` : "Upload Contacts"}
                              </Typography>
                              <Typography sx={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.3 }}>
                                {parsedExcelData.length ? "Click to choose a different file" : ".xlsx or .xls"}
                              </Typography>
                            </Box>
                            <VisuallyHiddenInput type="file" accept=".xlsx, .xls" onChange={(e) => { handleFileUpload(e); e.target.value = ""; }} />
                          </Box>
                        </Box>
                      ) : field.type === "selection" ? renderBranchDept(field)
                        : field.type === "multi-select" ? renderTags(field)
                          : null}
                    </Grid>
                  ))}
                </Grid>

                {/* Preview Table */}
                {parsedExcelData.length > 0 && (
                  <Box mt={3}>
                    <Typography sx={{ mb: 0.5, fontSize: 14, fontWeight: 800, color: "#0f172a" }}>
                      Preview <Box component="span" sx={{ ml: 0.5, fontSize: 12.5, fontWeight: 700, color: "#64748b" }}>({parsedExcelData.length} contacts)</Box>
                    </Typography>
                    <DataTableWithCheckbox
                      columns={columns}
                      data={parsedExcelData || []}
                      searchFirst
                      emptyMessage="No contacts in this file."
                    />
                  </Box>
                )}
              </Box>
              {submitBar}
            </>
          )}
        </Box>
      </Wrap>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}

// /contact-creation page
export default function ContactCreation() {
  return <ContactCreateForm />;
}
